import { useEffect, useRef, useState } from 'react'
import { Ic } from '../../../components/ui'
import { authenticatedJsonRequest } from '../../../shared/auth/authenticatedFetch'
import { useDashboardProfileForm } from '../hooks/useDashboardProfileForm'
import { LOCATIONS, PHONE_PLACEHOLDERS, PHONE_FORMATS, getMaskPrefix, applyPhoneMask, formatExistingPhone } from '../locationData'
import { Card, SectionTitle, Input, Select, PwInput, Toggle } from '../components/DashboardUI'

/* Phone input with mask formatting and ghost hint */
function PhoneInput({ value, onChange, onKeyDown, onFocus, placeholder, c }) {
  const [focus, setFocus] = useState(false)

  // Show the remaining untyped part of the placeholder as a muted ghost
  const ghostRemaining = placeholder && value !== undefined && value.length < placeholder.length
    ? placeholder.slice(value.length)
    : ''

  const FONT = "'JetBrains Mono',monospace"
  const PAD = '10px 14px'

  return (
    <div style={{
      position: 'relative',
      background: c.input,
      border: `1px solid ${focus ? c.teal : c.inputBorder}`,
      borderRadius: 10, transition: 'border-color .2s',
    }}>
      {/* Ghost hint layer — sits behind the input */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        padding: PAD, boxSizing: 'border-box',
        fontFamily: FONT, fontSize: 14, letterSpacing: '0.04em',
        display: 'flex', alignItems: 'center',
        pointerEvents: 'none', userSelect: 'none',
        overflow: 'hidden', whiteSpace: 'pre',
      }}>
        {/* Invisible spacer — same width as typed text */}
        <span style={{ visibility: 'hidden', whiteSpace: 'pre' }}>{value}</span>
        {/* Remaining format hint */}
        <span style={{ color: c.muted, opacity: 0.5 }}>{ghostRemaining}</span>
      </div>
      {/* Actual input — transparent background so ghost shows through */}
      <input
        type="tel"
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onFocus={e => { setFocus(true); onFocus?.(e) }}
        onBlur={() => setFocus(false)}
        style={{
          width: '100%', padding: PAD, boxSizing: 'border-box',
          background: 'transparent', border: 'none',
          borderRadius: 10, color: c.text,
          fontFamily: FONT, fontSize: 14, letterSpacing: '0.04em',
          outline: 'none', position: 'relative', zIndex: 1,
        }}
      />
    </div>
  )
}

export default function SettingsTab({ user, triggerToast, onProfileUpdate, c }) {
  const {
    firstName, lastName, birthDate, country, city, phone, isGoogleUser,
    setFirstName, setLastName, setBirthDate, setCountry, setCity, setPhone,
  } = useDashboardProfileForm(user)

  /* ── Phone mask ── */
  const phoneMask = PHONE_FORMATS[country] || ''
  const phonePrefix = phoneMask ? getMaskPrefix(phoneMask) : ''

  // On first load: format existing phone value from DB into the country's mask
  const initializedRef = useRef(false)
  useEffect(() => {
    if (initializedRef.current) return
    if (!country) return
    initializedRef.current = true
    const mask = PHONE_FORMATS[country]
    if (!mask) return
    if (phone) {
      const formatted = formatExistingPhone(phone, mask)
      if (formatted !== phone) setPhone(formatted)
    } else {
      setPhone(getMaskPrefix(mask))
    }
  }, [country]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Location handlers ── */
  const cityList = country ? (LOCATIONS[country] || []) : []

  const handleCountryChange = (e) => {
    const next = e.target.value
    setCountry(next)
    setCity('')
    const mask = PHONE_FORMATS[next]
    setPhone(mask ? getMaskPrefix(mask) : '')
  }

  /* ── Phone change handler ── */
  const handlePhoneChange = (e) => {
    const raw = e.target.value
    if (!phoneMask) { setPhone(raw); return }
    // Prevent deleting below prefix length
    if (raw.length < phonePrefix.length) { setPhone(phonePrefix); return }
    // Ensure prefix is intact
    if (!raw.startsWith(phonePrefix)) { setPhone(phone); return }
    // Extract the digits the user has typed (after the literal prefix)
    const userDigits = raw.slice(phonePrefix.length).replace(/\D/g, '')
    // If no digits yet, just show the prefix
    setPhone(userDigits ? applyPhoneMask(userDigits, phoneMask) : phonePrefix)
  }

  const handlePhoneKeyDown = (e) => {
    if (e.key === 'Backspace' && phone.length <= phonePrefix.length) {
      e.preventDefault()
    }
  }

  const handlePhoneFocus = (e) => {
    // Pre-fill prefix if field is empty
    if (!phone && phoneMask) setPhone(phonePrefix)
    // Move cursor to end
    setTimeout(() => {
      const el = e.target
      if (el) el.setSelectionRange(el.value.length, el.value.length)
    }, 0)
  }

  /* ── Profile save ── */
  const [savingProfile, setSavingProfile] = useState(false)

  const handleSaveProfile = async (e) => {
    e.preventDefault()
    setSavingProfile(true)
    try {
      await onProfileUpdate({
        firstName: firstName.trim(), lastName: lastName.trim(),
        birthDate: birthDate || null, country: country.trim(), city: city.trim(), phone: phone.trim(),
      })
    } finally { setSavingProfile(false) }
  }

  /* ── Password change ── */
  const [curPw, setCurPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [newPw2, setNewPw2] = useState('')
  const [savingPw, setSavingPw] = useState(false)

  const handleChangePw = async (e) => {
    e.preventDefault()
    if (!curPw || !newPw || !newPw2) { triggerToast('Заполните все поля', 'error'); return }
    if (newPw !== newPw2) { triggerToast('Новые пароли не совпадают', 'error'); return }
    if (newPw.length < 6) { triggerToast('Пароль минимум 6 символов', 'error'); return }
    setSavingPw(true)
    try {
      await authenticatedJsonRequest('/api/auth/password', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: curPw, newPassword: newPw, confirmPassword: newPw2 }),
      })
      triggerToast('Пароль изменён', 'ok')
      setCurPw(''); setNewPw(''); setNewPw2('')
    } catch (err) {
      triggerToast(err.message || 'Ошибка смены пароля', 'error')
    } finally { setSavingPw(false) }
  }

  /* ── Notifications ── */
  const [notif, setNotif] = useState({ email: true, sms: false, news: false })

  const lbl = (text) => (
    <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 10, fontWeight: 700, color: c.muted, letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 6 }}>{text}</div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Personal data */}
      <Card c={c}>
        <SectionTitle icon="user" label="Личные данные" c={c}/>
        <form onSubmit={handleSaveProfile} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="wl-form-grid">
            <div>{lbl('Имя')}<Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Имя" c={c}/></div>
            <div>{lbl('Фамилия')}<Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Фамилия" c={c}/></div>
            <div style={{ minWidth:0, overflow:'hidden' }}>{lbl('Дата рождения')}<Input type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} c={c}/></div>
            <div>
              {lbl('Телефон')}
              <PhoneInput
                value={phone}
                onChange={handlePhoneChange}
                onKeyDown={handlePhoneKeyDown}
                onFocus={handlePhoneFocus}
                placeholder={PHONE_PLACEHOLDERS[country] || '+7 (777) 777-77-77'}
                c={c}
              />
            </div>
            <div>{lbl('Страна')}<Select value={country} onChange={handleCountryChange} options={Object.keys(LOCATIONS)} placeholder="— Выберите страну —" c={c}/></div>
            <div>{lbl('Город')}<Select value={city} onChange={e => setCity(e.target.value)} options={cityList} placeholder={country ? '— Выберите город —' : '— Сначала выберите страну —'} disabled={!country} c={c}/></div>
          </div>
          <div>
            {lbl('Email адрес')}
            <Input value={user?.email || ''} disabled c={c}/>
            {user?.email && <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, fontSize: 12, color: c.muted }}>
              <Ic n="info" s={12}/>Email нельзя изменить после регистрации
            </div>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={savingProfile} style={{
              padding: '10px 24px', borderRadius: 10, border: 'none',
              background: `linear-gradient(135deg,${c.tealBright},${c.tealDeep})`,
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif", opacity: savingProfile ? .6 : 1, transition: 'opacity .2s',
            }}>
              {savingProfile ? 'Сохранение...' : 'Сохранить изменения'}
            </button>
          </div>
        </form>
      </Card>

      <div className={!isGoogleUser ? 'wl-2col' : ''} style={isGoogleUser ? { display: 'grid', gap: 16 } : undefined}>
        {/* Security */}
        {!isGoogleUser && (
          <Card c={c}>
            <SectionTitle icon="lock" label="Безопасность" c={c}/>
            <form onSubmit={handleChangePw} style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {lbl('Текущий пароль')}<PwInput value={curPw} onChange={e => setCurPw(e.target.value)} placeholder="Текущий пароль" c={c}/>
              {lbl('Новый пароль')}<PwInput value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Новый пароль" c={c}/>
              {lbl('Повторите пароль')}<PwInput value={newPw2} onChange={e => setNewPw2(e.target.value)} placeholder="Повторите новый пароль" c={c}/>
              <button type="submit" disabled={savingPw} style={{
                padding: '10px 20px', borderRadius: 10, border: `1px solid ${c.line}`,
                background: 'transparent', color: c.text, fontSize: 14, fontWeight: 600,
                cursor: 'pointer', fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif", opacity: savingPw ? .6 : 1, marginTop: 4,
              }}>
                {savingPw ? 'Сохранение...' : 'Изменить пароль'}
              </button>
            </form>
          </Card>
        )}

        {/* Notifications */}
        <Card c={c}>
          <SectionTitle icon="bell" label="Уведомления" c={c}/>
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { key: 'email', label: 'Email уведомления', desc: 'О проектах, заказах и изменениях' },
              { key: 'sms',   label: 'SMS уведомления',   desc: 'Важные уведомления на телефон' },
              { key: 'news',  label: 'Новости',           desc: 'Анонсы обновлений платформы' },
            ].map(({ key, label, desc }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: c.white }}>{label}</div>
                  <div style={{ fontSize: 12, color: c.muted }}>{desc}</div>
                </div>
                <Toggle on={notif[key]} set={v => setNotif(n => ({ ...n, [key]: v }))} c={c}/>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  )
}
